const KNOWN_CATALOG_MESSAGE_IDS = {
  'additional-downloading-documents': 'product_group.additional_downloading_documents',
  'additional-storage-electronic-archiving': 'product_group.additional_storage_electronic_archiving',
  certificates: 'product_group.certificate',
  'certificates-with-auth': 'product_group.certificate_with_auth',
  'custom-order': 'components.pages.reports.catalog.product.customOrder',
  'electronic-archiving-service': 'product_group.electronic_archiving_service',
  plus: 'components.pages.reports.catalog.product.plus',
  'qualified-signatures': 'product_group.qualified_signature',
  'simple-signatures': 'product_group.simple_signature',
  starter: 'components.pages.reports.catalog.product.starter'
};

// These exact business keys mirror Admin's ArchiveProductGroupPolicy and the
// ProductGroupTypes values used by the seeded commercial catalogue. Matching
// is intentionally exact: translated labels and partial slug/name matches are
// never used to infer archive behavior.
const ARCHIVE_PRODUCT_GROUP_KEYS = new Set([
  'electronic-archiving-service',
  'additional-storage-electronic-archiving',
  'additional-downloading-documents'
]);

const PRODUCT_GROUP_SECTION_MESSAGE_IDS = {
  certificates: 'components.pages.reports.catalog.section.certificates',
  signatures: 'components.pages.reports.catalog.section.signatures',
  archive: 'components.pages.reports.catalog.section.archive'
};

const PRODUCT_REPORT_SECTION_MESSAGE_IDS = {
  'certificates-with-auth': 'components.pages.reports.catalog.productSection.certificatesWithAuth',
  certificates: 'components.pages.reports.catalog.productSection.certificates',
  'qualified-signatures': 'components.pages.reports.catalog.productSection.qualifiedSignatures',
  'simple-signatures': 'components.pages.reports.catalog.productSection.simpleSignatures',
  archive: 'components.pages.reports.catalog.productSection.archive',
  other: 'components.pages.reports.catalog.productSection.other'
};

const PRODUCT_REPORT_SECTION_RANKS = {
  'certificates-with-auth': 0,
  certificates: 1,
  'qualified-signatures': 2,
  'simple-signatures': 3,
  archive: 4,
  other: 5
};

function normalizedLocale(locale) {
  return String(locale || 'ro').toLocaleLowerCase().split(/[-_]/)[0];
}

function translationLocale(translation) {
  const language = translation && translation.language;
  const languageData = language && language.data ? language.data : language;
  const locale = (languageData && languageData.short_name)
    || (translation && (translation.locale || translation.language_code))
    || '';
  return locale ? normalizedLocale(locale) : '';
}

function translatedName(entity, locale) {
  const translations = entity && Array.isArray(entity.translations) ? entity.translations : [];
  const requestedLocale = normalizedLocale(locale);
  const exact = translations.find(translation => translationLocale(translation) === requestedLocale);
  const english = translations.find(translation => translationLocale(translation) === 'en');
  const fallback = exact || english || translations[0];
  return fallback && fallback.name ? String(fallback.name).trim() : '';
}

function exactTranslatedName(entity, locale) {
  const translations = entity && Array.isArray(entity.translations) ? entity.translations : [];
  const requestedLocale = normalizedLocale(locale);
  const exact = translations.find(translation => translationLocale(translation) === requestedLocale);
  return exact && exact.name ? String(exact.name).trim() : '';
}

export function resolveKnownCatalogLabel(identifiers, formatMessage) {
  if (typeof formatMessage !== 'function') {
    return '';
  }

  const identity = (Array.isArray(identifiers) ? identifiers : [identifiers])
    .map(value => String(value || '').trim())
    .find(value => KNOWN_CATALOG_MESSAGE_IDS[value]);
  if (!identity) {
    return '';
  }

  const messageId = KNOWN_CATALOG_MESSAGE_IDS[identity];
  const formatted = formatMessage({ id: messageId });
  return formatted && formatted !== messageId ? formatted : '';
}

export function catalogEntityIdentifiers(entity) {
  if (!entity || typeof entity !== 'object') {
    return [];
  }

  return Array.from(new Set(['id', 'uuid', 'key', 'slug', 'name']
    .map(field => entity[field])
    .filter(value => value !== null && value !== undefined && String(value).trim() !== '')
    .map(String)));
}

export function humanizeCatalogKey(value) {
  const source = String(value || '').trim();
  if (!source) {
    return '';
  }

  if (!/[-_]/.test(source)) {
    return source;
  }

  const words = source.replace(/[-_]+/g, ' ').trim().split(/\s+/);
  return words.map((word, index) => {
    const normalized = word.toLocaleLowerCase();
    return index === 0
      ? `${normalized.charAt(0).toLocaleUpperCase()}${normalized.slice(1)}`
      : normalized;
  }).join(' ');
}

export function resolveCatalogLabel(entity, locale, formatMessage) {
  if (!entity) {
    return '';
  }

  const rawName = String(entity.name || entity.key || entity.slug || entity.id || '').trim();
  const exactTranslation = exactTranslatedName(entity, locale);
  if (exactTranslation) return exactTranslation;

  const canonical = resolveKnownCatalogLabel([rawName], formatMessage);
  if (canonical) return canonical;

  const explicit = entity.translated_name || entity.display_name;
  if (explicit) return String(explicit).trim();

  const translation = translatedName(entity, locale);
  if (translation) return translation;

  return humanizeCatalogKey(rawName);
}

export function catalogLabelsEqual(left, right, locale = 'ro') {
  return Boolean(left) && Boolean(right)
    && String(left).trim().toLocaleLowerCase(locale) === String(right).trim().toLocaleLowerCase(locale);
}

export function resolveProductCatalogLabel(product, productGroup, locale, formatMessage) {
  const productLabel = resolveCatalogLabel(product, locale, formatMessage);
  const groupLabel = resolveCatalogLabel(productGroup, locale, formatMessage);

  if (!groupLabel || catalogLabelsEqual(productLabel, groupLabel, locale)) {
    return productLabel || groupLabel;
  }

  return `${productLabel} · ${groupLabel}`;
}

export function isArchiveProductGroup(productGroup) {
  return Boolean(productGroup) && ARCHIVE_PRODUCT_GROUP_KEYS.has(String(productGroup.name || ''));
}

export function resolveProductGroupSection(productGroup, locale, formatMessage) {
  const name = String((productGroup && productGroup.name) || '');
  // The report has exactly three approved business sections. A catalog group
  // must never manufacture another top-level heading; until a new domain key
  // is explicitly classified, keep it in the certificate offering bucket.
  let key = 'certificates';

  if (isArchiveProductGroup(productGroup)) {
    key = 'archive';
  } else if (name === 'simple-signatures' || name === 'qualified-signatures') {
    key = 'signatures';
  }

  const messageId = PRODUCT_GROUP_SECTION_MESSAGE_IDS[key];
  return {
    key,
    label: typeof formatMessage === 'function' ? formatMessage({ id: messageId }) : messageId,
    rank: { certificates: 0, signatures: 1, archive: 2 }[key]
  };
}

/** Products tables use the complete commercial taxonomy. Unknown groups are
 * retained in one deterministic final section rather than being mislabeled as
 * certificates or omitted from the report. */
export function resolveProductReportSection(productGroup, locale, formatMessage) {
  const name = String((productGroup && productGroup.name) || '');
  let key = Object.prototype.hasOwnProperty.call(PRODUCT_REPORT_SECTION_RANKS, name)
    ? name
    : 'other';

  if (isArchiveProductGroup(productGroup)) {
    key = 'archive';
  }

  const messageId = PRODUCT_REPORT_SECTION_MESSAGE_IDS[key];
  return {
    key,
    label: typeof formatMessage === 'function' ? formatMessage({ id: messageId }) : messageId,
    rank: PRODUCT_REPORT_SECTION_RANKS[key]
  };
}

export {
  ARCHIVE_PRODUCT_GROUP_KEYS,
  KNOWN_CATALOG_MESSAGE_IDS,
  PRODUCT_REPORT_SECTION_MESSAGE_IDS,
  PRODUCT_REPORT_SECTION_RANKS,
  PRODUCT_GROUP_SECTION_MESSAGE_IDS
};
